import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  items: [],
  currencies: [],
  status: "idle",
  error: null,
  success: null,
  loading: false,
  lastRestaurantId: null,
};

export const getSettings = createAsyncThunk(
  "settings/getSettings",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/settings`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addSettings = createAsyncThunk(
  "settings/addSettings",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/settings`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);
export const getRestaurantCurrencies = createAsyncThunk(
  "settings/getRestaurantCurrencies",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/settings/currency`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const updateCurrency = createAsyncThunk(
  "settings/updateCurrency",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/settings/currency`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const updateSetting = createAsyncThunk(
  "settings/updateSetting",
  async ({ formData, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/settings`,
        formData,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);
export const deleteSettings = createAsyncThunk(
  "settings/deleteSettings",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.delete(`${apiUrl}/settings/currency`, {
        data: body,
        ...getAuthHeaders(false, restaurantId),
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const settingSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSettings.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(getSettings.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
        state.lastRestaurantId = action.meta.arg;
      })
      .addCase(getSettings.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addSettings.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addSettings.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addSettings.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteSettings.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteSettings.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteSettings.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateSetting.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.status = "updateSettingSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(updateSetting.rejected, (state, action) => {
        state.status = "updateSettingError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getRestaurantCurrencies.pending, (state) => {
        state.status = "loadingCurrencies";
        state.loading = true;
      })
      .addCase(getRestaurantCurrencies.fulfilled, (state, action) => {
        state.status = "fetchCurrencies";
        state.loading = false;
        state.currencies = action.payload?.currencies || [];
        if (action.payload?.defaultCurrency) {
          state.items = {
            ...(state.items || {}),
            defaultCurrency: action.payload.defaultCurrency,
          };
        }
      })
      .addCase(getRestaurantCurrencies.rejected, (state, action) => {
        state.status = "fetchCurrenciesError";
        state.loading = false;
        state.error = action.error.message;
        state.currencies = [];
      })
      .addCase(updateCurrency.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(updateCurrency.fulfilled, (state, action) => {
        state.status = "updateSuccess";
        state.loading = false;
        state.success = action.payload.message;
        if (action.payload?.defaultCurrency) {
          state.items = {
            ...(state.items || {}),
            defaultCurrency: action.payload.defaultCurrency,
          };
        }
      })
      .addCase(updateCurrency.rejected, (state, action) => {
        state.status = "updateError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});
export const { updateStatus } = settingSlice.actions;
export const selectAllSettings = (state) => state.settings.items;
export const selectRestaurantCurrencies = (state) => state.settings.currencies || [];
export const getSettingsStatus = (state) => state.settings.status;
export const selectSettingsLastRestaurantId = (state) =>
  state.settings.lastRestaurantId;
export const getSettingsError = (state) => state.settings.error;
export const getSettingsSuccess = (state) => state.settings.success;
export const getSettingsLoading = (state) => state.settings.loading;
export default settingSlice.reducer;
